import { CATEGORY, emptyConsent } from '@/constants/cookie-registry'

export const CONSENT_COOKIE_NAME = 'renew_consent'

const COOKIE_VERSION = 1
// Chrome caps any cookie at 400 days, so a longer max-age is silently truncated.
// The 3-year retention from the spec applies to the server-side consent log, not to this cookie.
const COOKIE_MAX_AGE_SECONDS = 400 * 24 * 60 * 60

// Every value carrying this name, not just the first. The backend now sets its own
// `renew_consent` on the domain shared by the site and the API, while the write below stays
// host-only, so two cookies of the same name coexist. The order `document.cookie` returns them
// in is not specified between a domain cookie and a host-only one, and `.find()` picked
// whichever happened to come first - a decision that reads correctly on one browser and comes
// back stale on the next.
function readRawCookies(name) {
  try {
    const prefix = name + '='

    return document.cookie
      .split('; ')
      .filter((row) => row.indexOf(prefix) === 0)
      .map((row) => decodeURIComponent(row.slice(prefix.length)))
  } catch (e) {
    return []
  }
}

function parseConsentCookie(raw) {
  let parsed = null
  try {
    parsed = JSON.parse(raw)
  } catch (e) {
    return null
  }

  if (!parsed || typeof parsed !== 'object' || !parsed.cat) return null

  const expiresAt = parsed.exp ? Date.parse(parsed.exp) : NaN
  const isExpired = !isNaN(expiresAt) && expiresAt <= Date.now()

  return {
    id: parsed.id || null,
    expiresAt: parsed.exp || null,
    cookieListVersion: parsed.clv || null,
    bannerVersion: parsed.bv || null,
    isExpired,
    categories: {
      ...emptyConsent(),
      [CATEGORY.ANALYTICS]: Boolean(parsed.cat[CATEGORY.ANALYTICS]),
      [CATEGORY.MARKETING]: Boolean(parsed.cat[CATEGORY.MARKETING]),
    },
  }
}

export function readConsentState() {
  // Both cookies carry the same shape, so this is not a judgement about which source is more
  // trustworthy - it is a tie-break that always lands on the same one. The furthest expiry is
  // the most recently written decision: every write derives `exp` from the moment it happens.
  const candidates = readRawCookies(CONSENT_COOKIE_NAME)
  let best = null

  for (let i = 0; i < candidates.length; i++) {
    const state = parseConsentCookie(candidates[i])
    if (!state) continue

    if (!best || Date.parse(state.expiresAt) > Date.parse(best.expiresAt)) {
      best = state
    }
  }

  return best
}

export function hasValidConsent(state) {
  const current = state === undefined ? readConsentState() : state
  return Boolean(current && !current.isExpired)
}

export function readConsentCategories() {
  const state = readConsentState()
  return hasValidConsent(state) ? state.categories : emptyConsent()
}

export function readConsentId() {
  const state = readConsentState()
  return state ? state.id : null
}

export function writeConsentState({ id, expiresAt, categories, cookieListVersion, bannerVersion }) {
  const payload = {
    v: COOKIE_VERSION,
    id: id || null,
    exp: expiresAt || null,
    clv: cookieListVersion || null,
    bv: bannerVersion || null,
    cat: {
      [CATEGORY.ANALYTICS]: Boolean(categories && categories[CATEGORY.ANALYTICS]),
      [CATEGORY.MARKETING]: Boolean(categories && categories[CATEGORY.MARKETING]),
    },
  }

  try {
    const value = encodeURIComponent(JSON.stringify(payload))
    const secure = location.protocol === 'https:' ? '; secure' : ''
    document.cookie =
      CONSENT_COOKIE_NAME +
      '=' +
      value +
      '; max-age=' +
      COOKIE_MAX_AGE_SECONDS +
      '; path=/; samesite=lax' +
      secure
  } catch (e) {
    return
  }
}

// Rewrites the cookie unchanged so its 400-day cap starts counting again. Chrome, Firefox and
// Safari all measure that cap from the moment the cookie is set, so a returning visitor keeps
// the same chain indefinitely instead of losing it on day 400. Deliberately preserves `exp` as
// it stands, including an expired one: a lapsed consent still has to show the banner, but its
// consent_id has to survive so the next decision continues the chain rather than starting a
// second one for the same person.
export function refreshConsentState() {
  const state = readConsentState()
  if (!state) return false

  writeConsentState({
    id: state.id,
    expiresAt: state.expiresAt,
    categories: state.categories,
    cookieListVersion: state.cookieListVersion,
    bannerVersion: state.bannerVersion,
  })

  return true
}

export function clearConsentState() {
  try {
    document.cookie = CONSENT_COOKIE_NAME + '=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/'
  } catch (e) {
    return
  }
}
